// @vendors
import React, { useContext, useMemo, useState } from 'react';

// @state
import { useUI } from '../../../app/context/ui.js';

// @material-ui
import {
  Box,
  Button,
  TextField,
  Typography,
} from '../../../components/shared/MaterialUI.js';

// @helpers
import AppHelper from '../../../helpers/AppHelper.js';

// @services
import QuestionnaireServiceNewApi from '../../../services/newApi/QuestionnaireServiceNewApi.js';

// @components
import { QuestionnaireContext } from '../Prescreening.js';

// @assets
import { theme } from '../../../assets/theme.js';

const PinVerification = () => {
  const {
    hashToken,
    lang,
    setDemographicData,
    setNeedsPin,
  } = useContext(QuestionnaireContext);

  const { blockUI, snackbarUI } = useUI();
  const questionnaireService = useMemo(() => new QuestionnaireServiceNewApi(), []);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countryCode, setCountryCode] = useState(process.env.REACT_APP_COUNTRY_CODE || '1');

  const handleSendCode = async () => {
    try {
      blockUI.current.open(true);
      await questionnaireService.sendConfirmationCode(hashToken, countryCode);
      setCodeSent(true);
      setError('');
      blockUI.current.open(false);
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  };

  const handleVerify = async () => {
    if (!pin || pin.length < 4) {
      setError(lang === 'es' ? 'Ingrese el código' : 'Please enter the code');
      return;
    }
    try {
      blockUI.current.open(true);
      const resp = await questionnaireService.confirmDemographics(hashToken, pin, countryCode);
      const data = resp?.data?.data;
      if (data) {
        setDemographicData(data);
      }
      setNeedsPin(false);
      blockUI.current.open(false);
    } catch (e) {
      blockUI.current.open(false);
      const msg = e?.response?.data?.message || e?.response?.data?.errors?.[0];
      if (msg) {
        setError(msg);
      } else {
        AppHelper.checkError(e, snackbarUI);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: theme.palette.primary.black,
          mb: 1,
        }}
      >
        {lang === 'es' ? 'Verificación de Identidad' : 'Identity Verification'}
      </Typography>

      <Typography
        sx={{
          fontSize: '1rem',
          color: theme.palette.secondary.black,
          mb: 3,
        }}
      >
        {codeSent
          ? (lang === 'es'
            ? 'Ingrese el código que fue enviado a su celular'
            : 'Enter the code that was sent to your phone')
          : (lang === 'es'
            ? 'Para continuar, enviaremos un código de verificación a su celular'
            : 'To continue, we will send a verification code to your phone')
        }
      </Typography>

      {!codeSent ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            onClick={handleSendCode}
            sx={{
              width: '100%',
              maxWidth: '400px',
              height: '52px',
              backgroundColor: theme.palette.primary.black,
              color: theme.palette.primary.white,
              textTransform: 'none',
              fontSize: '1.125rem',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.secondary.black,
                boxShadow: 3,
              },
            }}
          >
            {lang === 'es' ? 'Enviar Código' : 'Send Code'}
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <TextField
              value={pin}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setPin(val);
                setError('');
              }}
              placeholder={lang === 'es' ? 'Ingrese el código' : 'Enter code'}
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
              sx={{
                width: '250px',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Box>

          {error && (
            <Typography sx={{ color: theme.palette.error.main, mb: 2, fontSize: '0.875rem' }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Button
              onClick={handleVerify}
              sx={{
                width: '100%',
                maxWidth: '400px',
                height: '52px',
                backgroundColor: theme.palette.primary.black,
                color: theme.palette.primary.white,
                textTransform: 'none',
                fontSize: '1.125rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.black,
                  boxShadow: 3,
                },
              }}
            >
              {lang === 'es' ? 'Verificar' : 'Verify'}
            </Button>

            <Button
              onClick={handleSendCode}
              sx={{
                textTransform: 'none',
                color: theme.palette.primary.main,
                fontSize: '0.875rem',
              }}
            >
              {lang === 'es' ? 'Reenviar código' : 'Resend code'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PinVerification;
